import { ICertificateFullInfo, ITransferInfo } from '@repo/types';
import { useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { Button } from '@repo/ui';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { ScanTag } from '../../ScanTag';
import { useAcceptTransfer } from 'hooks/api/transfers/useAcceptTransfer';
import { GenericSuccessDrawer } from 'components/genericSuccessDrawer';
import { useCancelTransfer } from 'hooks/api/transfers/useCancelTransfer';

/**
 * VERA-304 (A7): the copy the user reads when a cancel does not go through.
 *
 * It is OURS, unconditionally — the server's `data.message` is never rendered
 * here. That is the client half of the fix; the API half is
 * `ApplicationExceptionFilter`, which used to serialise a driver error's
 * message and stack into the body and put a live `INSERT INTO "transfers" (…)`
 * on this screen. Both halves are needed and neither is sufficient: the filter
 * stops the leak at source for every surface, and this stops THIS surface
 * rendering whatever some future endpoint decides to put in `message`.
 *
 * `useGetCertificates` already reached the same conclusion for 5xx ("a 5xx gets
 * OUR copy, never the server's `data.message`"); this applies it to the cancel.
 */
const CANCEL_FAILURE_MESSAGE = 'We could not cancel this gift. Please try again.';

export const GiftOutActionsSection = ({
  certificate,
  shouldShowOnCertificatePage,
}: {
  certificate: ICertificateFullInfo;
  shouldShowOnCertificatePage: boolean;
}) => {
  const [isScanTagDrawerOpen, setIsScanTagDrawerOpen] = useState(false);
  const [isSuccessDrawerOpen, setIsSuccessDrawerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: cancelTransfer } = useCancelTransfer({
    certificateId: certificate?.id ?? '',
  });

  const transfer = certificate.transfer as ITransferInfo & { code: string };

  /**
   * VRG-323 — the brand's already-resolved scan instructions + media, forwarded to
   * ScanTag so "Where to scan?" shows this item's real content.
   *
   * Read through a structural widening rather than off `ICertificateInfoProduct`
   * directly. That type is `Required<Omit<IProduct, …>>` in `@repo/types`, a
   * package this app-layer change deliberately does not touch — the API work adding
   * the two columns to `IProduct` is landing concurrently, and editing the shared
   * package from here would collide with it. The widening compiles both before and
   * after that lands: intersecting optional `string | null` members with the
   * eventual required ones is compatible either way, so nothing has to be unwound.
   */
  const product = certificate.product as typeof certificate.product & {
    scan_instructions?: string | null;
    scan_media_url?: string | null;
  };

  return (
    <>
      {/*
        The action renders HERE, inside the state card, not in a pinned bottom bar.

        It used to portal into the page's StickyActionBar, which put a bare verb a
        full screen away from the block explaining it: "Cancel gift" alone does not
        say which gift, to whom, or that it is still unclaimed — and this card says
        all three. The explanation and the action that resolves it belong together;
        separating them was the defect, and relocating the button was the fix.

        Only the pixels moved. The mutation, the scan-tag drawer and the success
        drawer it drives all still live in this component.
      */}
      {/* FULL WIDTH at the bottom of the card — the shape every state card
          shares, so gift-out, gift-in and transfer-in read as one family rather
          than three arrangements. It stays SECONDARY: this is the card's action,
          not the page's primary CTA, and it is usually a reversal (cancel, deny),
          which a solid fill oversells. */}
      {shouldShowOnCertificatePage && (
          <Button
            variant='secondary'
            className='w-full'
            sx={{ paddingX: 2 }}
            onClick={() => setIsScanTagDrawerOpen(true)}
          >
            Cancel gift
          </Button>
      )}
      <ScanTag
        title='Cancel gift'
        pageTitle='Scan to cancel gift'
        pageDescription='Hold your phone near the item to scan the tag and confirm gift cancelation.'
        // VRG-323: `pageDescription` above deliberately still wins on the scan
        // screen itself — it names the TASK (confirming a cancelation), which the
        // brand's tag-location copy does not. The instructions and media are
        // reached from this drawer's "Where to scan?" control instead.
        scanInstructions={product?.scan_instructions}
        scanMediaUrl={product?.scan_media_url}
        open={isScanTagDrawerOpen}
        onClose={() => {
          setIsScanTagDrawerOpen(false);
        }}
        onScanSuccess={async () => {
          // VERA-304: `mutate` is FIRE-AND-FORGET — it returns immediately and never
          // throws synchronously, so the `try/catch` this replaces caught nothing and
          // `setIsSuccessDrawerOpen(true)` ran unconditionally. A failed cancel
          // therefore rendered "You have successfully canceled the gift" while the
          // transfer stayed `initiated` and the recipient could still claim it. That
          // is the worst failure shape available here: the sender believes the gift
          // is revoked and stops watching it.
          //
          // Observed live, not theorised: POST .../transfer/customer/cancel returned
          // 400 three times (react-query retried) behind a success drawer.
          //
          // `mutateAsync` + await makes the promise real, so the drawer opens only
          // when the server actually cancelled, and a failure surfaces in the
          // "Gift not canceled" modal below.
          setError(null);
          try {
            await cancelTransfer(certificate.id ?? '');
            setIsSuccessDrawerOpen(true);
          } catch {
            // VERA-304 (A7): the thrown error is DISCARDED, deliberately. It is an
            // axios error whose `response.data.message` is server-authored, and
            // rendering it is what put raw SQL on this screen. There is nothing in
            // it a gift sender can act on; the actionable facts (it did not cancel,
            // the gift is still live) are known here without reading it.
            setIsScanTagDrawerOpen(false);
            setError(CANCEL_FAILURE_MESSAGE);
          }
        }}
      />
      {/*
        VERA-304 (A7): a failed cancel is a MODAL, not an inline Alert and not a toast.

        §43's axis: a toast is the acknowledgement of a VISIBLE action that
        succeeded; a modal carries a consequence the user has to act on. This is
        squarely the second. The sender pressed "Cancel gift", watched a scan
        succeed, and the gift is nonetheless STILL LIVE — the recipient can still
        claim it. An Alert at the bottom of a card is dismissable by scrolling past
        it, which is exactly the failure shape the `mutateAsync` fix above was
        written to close: the sender believes the gift is revoked and stops
        watching it.

        Reuses `DrawerPopup` (§28/§47) rather than a hand-rolled sheet — the same
        component the sibling gift-in "Decline Gift" confirmation uses, so the two
        halves of this flow read as one family. It is modal by construction (MUI
        Drawer with a backdrop on mobile, Dialog on desktop).

        §52 is satisfied: the ScanTag drawer is closed in the catch above BEFORE
        this opens, so this is one sheet, never a second stacked on the scan.
      */}
      <DrawerPopup
        title='Gift not canceled'
        handleOpen={Boolean(error)}
        handleClose={() => setError(null)}
        child={
          <Stack sx={{ pt: 1, pb: 2 }}>
            {/* §15: separate sentences rather than em-dash-joined clauses, and
                lineHeight 1.5 so short body copy does not read dense. The second
                sentence is the one that matters — it names what is still true. */}
            <Typography
              variant='body2'
              sx={{ color: 'text.secondary', lineHeight: 1.5, mb: 3 }}
            >
              {error} This gift is still active, and it can still be claimed until
              the cancelation goes through.
            </Typography>
            <Button
              className='w-full'
              variant='primary'
              sx={{ paddingX: 2 }}
              onClick={() => {
                setError(null);
                setIsScanTagDrawerOpen(true);
              }}
            >
              Try again
            </Button>
            <Button
              className='w-full mt-3'
              variant='secondary'
              sx={{ paddingX: 2 }}
              onClick={() => setError(null)}
            >
              Close
            </Button>
          </Stack>
        }
      />
      <GenericSuccessDrawer
        open={isSuccessDrawerOpen}
        onClose={() => {
          setIsSuccessDrawerOpen(false);
          setIsScanTagDrawerOpen(false);
        }}
        certificate={certificate}
        drawerTitle='Gift canceled'
        bodyTitle='Gift canceled'
        description={`You have successfully canceled the gift.`}
        buttonLabel='View certificate'
      />
    </>
  );
};
